/**
 * Copyright (c) 2021-2024 Kognitos, Inc. All rights reserved.
 */
import useMergeState from '@/utils/reactUtils';
import * as React from 'react';
import { Button, Checkbox, Flex, Form, Input, Tabs, message } from 'antd';
import { useMutation } from '@apollo/client';
import {
  CredentialValue,
  CredentialValueInput,
  GenerateIntegrationAuthorizationUrlMutation,
  GenerateIntegrationAuthorizationUrlMutationVariables,
  LearnBooksMutation,
  StoreIntegrationCredentialMutation,
  StoreIntegrationCredentialsMutation,
  useStoreIntegrationCredentialMutation
} from '@/generated/graphql';
import { capitalize, isEmpty } from 'lodash';
import { useAppSelector } from '@/stores/hooks';
import { departmentQuerySelector } from '@/stores/slices/department';
import {
  GENERATE_AUTHORIZATION_URL,
  LEARN_BOOKS,
  STORE_INTEGRATION_CREDENTIALS
} from '@/graphql/mutations/books';
import { LIST_INTEGRATION_BY_DEPARTMENT } from '@/graphql/queries/books';
import { useEffect, useState } from 'react';
import { CheckboxChangeEvent } from 'antd/es/checkbox';

import { ApolloError } from '@apollo/react-hooks';
import classnames from 'classnames';
import useALCFlag from '@/hooks/useALCFlag';
import styles from './IntegrationForm.module.less';

interface IntegrationFormFields {
  label: string;
  // eslint-disable-next-line react/no-unused-prop-types
  key: string;
  type: string;
  description?: string | null;
  example?: string | null;
}

interface IFormField extends IntegrationFormFields {
  itemKey: string;
  value?: any;
}
interface IntegrationFormProps {
  integrationId: string;
  fields: IntegrationFormFields[];
  onclose: () => void;
  credentialData?: CredentialValue[];
  onIntegration?: (integrationId: string) => void;
  workerId?: string;
  procedureId?: string;
  classname?: string;
  readonly?: boolean;
  formLayout?: 'horizontal' | 'vertical';
}

interface FormFieldProps extends IFormField {
  envName: string;
  onChange?: (
    envName: string,
    key: string,
    label: string,
    value: string
  ) => void;
  readonly?: boolean;
  className?: string;
}

interface FormValue {
  label: string;
  value: string; // or any other type that `value` is supposed to be
}

interface FormValues {
  [key: string]: FormValue;
}

const processFormValues = (formValues: FormValues) => {
  return Object.entries(formValues).map(([key, value]) => ({
    key,
    value: value.value,
    label: value.label
  }));
};

export function FormField(props: FormFieldProps) {
  const {
    label,
    itemKey,
    type,
    description,
    example,
    value,
    readonly,
    onChange,
    className,
    envName
  } = props;

  const handleChange = (itemKey: string, label: string, value: string) => {
    if (onChange) {
      onChange(envName, itemKey, label, value);
    }
  };
  return (
    <Form.Item
      className={classnames(styles.formField, className)}
      hasFeedback={!readonly}
      colon={false}
      labelAlign="left"
      help={!readonly && description}
      label={label}
      name={itemKey}
      rules={[{ required: !readonly }]}
      data-cy={`form-field-${envName}-${itemKey}`}
    >
      <Input
        placeholder={example!}
        type={type}
        readOnly={readonly}
        value={value}
        onChange={(e) => handleChange(itemKey, label, e.target.value)}
        data-cy={`form-field-input-${envName}-${itemKey}`}
        data-private
        defaultValue={value}
      />
    </Form.Item>
  );
}

interface StageIntergrationFormState {
  [key: string]: FormValues;
}

export enum IntegrationStages {
  'Test' = 'test',
  'Production' = 'production'
}

const IntegrationForm = (props: IntegrationFormProps) => {
  const {
    fields,
    onclose,
    integrationId,
    onIntegration,
    credentialData,
    workerId,
    procedureId,
    classname,
    readonly,
    formLayout
  } = props;
  const { department } = useAppSelector(departmentQuerySelector);
  const { isALCEnabled } = useALCFlag();

  const [activeEnv, setActiveEnv] = useState<string>(IntegrationStages.Test);
  const [submitActive, setSubmitActive] = useState<boolean>(true);

  // Separate states for staging and production
  const [formStates, setFormStates] = useMergeState<StageIntergrationFormState>(
    {}
  );
  const [oldFormStates, setOldFormStates] =
    useMergeState<StageIntergrationFormState>({});

  const [storeIntegrations, { loading }] = useMutation(
    STORE_INTEGRATION_CREDENTIALS
  );

  const [storeIntegration] = useStoreIntegrationCredentialMutation();

  const [learnBook] = useMutation<LearnBooksMutation>(LEARN_BOOKS);
  const [generateAuthUrl] =
    useMutation<GenerateIntegrationAuthorizationUrlMutation>(
      GENERATE_AUTHORIZATION_URL
    );

  useEffect(() => {
    const formStateData = Object.keys(IntegrationStages).reduce(
      (acc: StageIntergrationFormState, stage: string) => {
        const lowerCaseStage = stage.toLowerCase();
        acc[lowerCaseStage] = {};
        return acc;
      },
      {}
    );
    setFormStates(formStateData);
    setOldFormStates(formStateData);
  }, []);

  useEffect(() => {
    if (!isALCEnabled) {
      setActiveEnv(IntegrationStages.Production);
    }
  }, [isALCEnabled]);

  useEffect(() => {
    const isFormEmpty = Object.values(formStates).some((form) =>
      // eslint-disable-next-line lodash-fp/no-extraneous-function-wrapping
      Object.values(form).some((value) => isEmpty(value))
    );
    setSubmitActive(!isFormEmpty);
  }, [formStates]);

  // Function to process inner data of each credential item
  const processItemData = (item: CredentialValue) => {
    return item?.data?.reduce(
      (acc, curr: any) => {
        return {
          oldValues: {
            ...acc.oldValues,
            [curr.key]: curr.value
          },
          editValues: {
            ...acc.editValues,
            [curr.key]: {
              value: curr.value,
              label: curr.label
            }
          }
        };
      },
      { oldValues: {}, editValues: {} }
    );
  };

  // Function to transform credential data
  const transformCredentialData = (credentialData: CredentialValue[]) => {
    return credentialData.reduce((acc, item) => {
      const innerData = processItemData(item);

      const key = isEmpty(item?.name)
        ? IntegrationStages.Production
        : (item?.name! as string);

      return { ...acc, [key]: innerData };
    }, {});
  };

  // Function to extract specific values from each key in the data object
  const extractFormValuesFromData = (
    data: Record<string, any>,
    propertyName: 'oldValues' | 'editValues'
  ) => {
    return Object.keys(data).reduce((acc: StageIntergrationFormState, key) => {
      acc[key] = { ...data[key][propertyName] };
      return acc;
    }, {});
  };

  useEffect(() => {
    if (credentialData) {
      const transformedData = transformCredentialData(credentialData);
      const editValues = extractFormValuesFromData(
        transformedData,
        'editValues'
      );
      const oldValues = extractFormValuesFromData(transformedData, 'oldValues');

      setFormStates(editValues);
      setOldFormStates(oldValues);
    }
  }, [credentialData]);

  const handleChange = (
    environment: string,
    key: string,
    label: string,
    value: string
  ) => {
    const newState = { ...formStates };
    newState[environment] = {
      ...newState[environment],
      [key]: { value, label }
    };
    setFormStates(newState);
  };

  const handleError = (context: string, error: ApolloError) =>
    message.error(`${context}, ${error.message}`);

  const handleRedirection = () => {
    // TODO: May not work after ALC, need to be fixed, but we are okay for now as none of the customer is using oAuth flow in production
    message.loading('Generating Authentication Url');
    const variables: GenerateIntegrationAuthorizationUrlMutationVariables = {
      integrationId,
      departmentId: department.id
    };
    if (workerId) {
      variables.workerId = workerId;
    }
    if (procedureId) {
      variables.procedureId = procedureId;
    }

    generateAuthUrl({
      variables,
      onCompleted: (data) => {
        message.success('Redirecting to authorization page');
        window.open(data.generateIntegrationAuthorizationUrl?.url!, '_self');
      },
      onError: (error) =>
        handleError('Unable to generate authorization url', error)
    });
  };

  const handleComplete = (
    data:
      | StoreIntegrationCredentialsMutation
      | StoreIntegrationCredentialMutation
  ) => {
    const completionData = isALCEnabled
      ? (data as StoreIntegrationCredentialsMutation)
          ?.storeIntegrationCredentials
      : (data as StoreIntegrationCredentialMutation)
          ?.storeIntegrationCredential;
    learnBook({
      variables: {
        ids: [integrationId],
        departmentId: department.id
      },
      onCompleted: () => {
        if (!completionData?.redirectionFlowRequired) {
          message.success(`Book integrated successfully`);
        }
        if (!completionData?.redirectionFlowRequired && onIntegration) {
          onIntegration(integrationId);
        }
      },
      refetchQueries: [
        {
          query: LIST_INTEGRATION_BY_DEPARTMENT,
          variables: {
            departmentId: department.id
          }
        }
      ],
      onError: (error) => {
        handleError(`Unable to learn book`, error);
      }
    });
  };

  const handleSubmit = (env: string) => {
    // TODO: This will not work in case of OAuthFlow, need to rethink this properly
    if (env !== IntegrationStages.Production && !isEmpty(formStates[env])) {
      setActiveEnv(IntegrationStages.Production);
      return;
    }

    if (!isEmpty(formStates[env])) {
      if (isALCEnabled) {
        const variableDataInput: CredentialValueInput[] = Object.keys(
          formStates
        ).reduce((acc: CredentialValueInput[], key) => {
          if (isEmpty(formStates[key])) {
            return acc;
          }

          acc.push({
            integrationId,
            departmentId: department.id,
            name: key,
            data: processFormValues(formStates[key])
          });
          return acc;
        }, []);

        storeIntegrations({
          variables: {
            input: variableDataInput
          },

          onCompleted: (data: StoreIntegrationCredentialsMutation) => {
            handleComplete(data);
            if (data.storeIntegrationCredentials?.redirectionFlowRequired) {
              handleRedirection();
            }
          },
          onError: (error) => {
            handleError(`Unable to store integration credentials`, error);
          }
        });
      } else {
        // TODO: remove when ALC is available to all customers
        const variableDataInput = {
          integrationId,
          departmentId: department.id,
          data: processFormValues(formStates[IntegrationStages.Production]),
          name: IntegrationStages.Production
        };
        storeIntegration({
          variables: {
            input: variableDataInput
          },
          onCompleted: (data) => {
            handleComplete(data);
            if (data.storeIntegrationCredential?.redirectionFlowRequired) {
              handleRedirection();
            }
          },
          onError: (error) => {
            handleError(`Unable to store integration credentials`, error);
          }
        });
      }
    }
  };

  const handleCancel = () => {
    onclose();
  };

  const handleStageToProdCopy = (e: CheckboxChangeEvent) => {
    if (e.target.checked) {
      const newState = { ...formStates };
      newState[IntegrationStages.Production] = {
        ...formStates[IntegrationStages.Test]
      };
      setFormStates(newState);
    }
  };

  if (isEmpty(oldFormStates) && isEmpty(formStates)) {
    return null;
  }

  return (
    <div
      className={styles.integrationForm}
      data-cy={`integration-form-${integrationId}`}
    >
      {isALCEnabled ? (
        <Tabs
          defaultActiveKey={IntegrationStages.Test}
          activeKey={activeEnv}
          onChange={(key) => setActiveEnv(key)}
          data-cy={readonly ? 'integration-info-tabs' : 'integration-form-tabs'}
          items={Object.keys(formStates).map((item) => {
            return {
              label: capitalize(item),
              key: item,
              children: (
                <div
                  className={classnames(
                    styles.integrationFormContainer,
                    classname
                  )}
                >
                  <Form
                    layout={formLayout || 'vertical'}
                    initialValues={oldFormStates[item]}
                  >
                    {fields?.map((field) => (
                      <FormField
                        {...field}
                        readonly={readonly}
                        envName={item}
                        itemKey={field.key}
                        onChange={handleChange}
                        value={formStates?.[item]?.[field.key]?.value}
                      />
                    ))}
                  </Form>
                  {item === IntegrationStages.Test &&
                    isEmpty(formStates[IntegrationStages.Production]) && (
                      <Checkbox
                        onChange={handleStageToProdCopy}
                        disabled={isEmpty(formStates[item])}
                        data-cy="copy-test-credentials"
                      >
                        Copy test credentials to production
                      </Checkbox>
                    )}
                </div>
              )
            };
          })}
        />
      ) : (
        <div className={classnames(styles.formFieldContainer, classname)}>
          <Form
            layout={formLayout || 'vertical'}
            initialValues={oldFormStates[IntegrationStages.Production]}
          >
            {fields?.map((field) => (
              <FormField
                {...field}
                readonly={readonly}
                itemKey={field.key}
                envName={IntegrationStages.Production}
                onChange={handleChange}
                value={
                  formStates?.[IntegrationStages.Production]?.[field.key]?.value
                }
              />
            ))}
          </Form>
        </div>
      )}

      {!readonly ? (
        <Flex vertical align="end">
          <div className={styles.submitContainer}>
            <Button
              type="default"
              onClick={handleCancel}
              className={styles.cancelBtn}
              data-cy="learn-book-cancel"
            >
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={() => handleSubmit(activeEnv)}
              disabled={
                loading ||
                (activeEnv === IntegrationStages.Test &&
                  isEmpty(formStates[IntegrationStages.Test])) ||
                (activeEnv === IntegrationStages.Production && !submitActive)
              }
              className={styles.submitBtn}
              data-cy={
                activeEnv === IntegrationStages.Production
                  ? `learn-book-submit`
                  : `learn-book-next`
              }
            >
              {activeEnv === IntegrationStages.Production ? 'Submit' : 'Next'}
            </Button>
          </div>
        </Flex>
      ) : null}
    </div>
  );
};

export default IntegrationForm;
